import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share, X, Plus } from "lucide-react";

const DISMISS_KEY = "renova_a2hs_dismissed";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const inIframe = () => { try { return window.self !== window.top; } catch { return true; } };

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (inIframe() || isStandalone() || !isMobile()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari has no beforeinstallprompt — show manual instructions.
    let t;
    if (isIOS()) {
      t = setTimeout(() => { setIosHint(true); setShow(true); }, 1500);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) {}
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 inset-x-4 z-[90] lg:hidden" data-testid="install-prompt">
      <div className="mx-auto max-w-md bg-white border border-gray-200 rounded-xl shadow-lg p-4 flex items-start gap-3">
        <img src="/renova-icon.png" alt="" className="w-10 h-10 object-contain shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Install Renova Vault</div>
          {iosHint ? (
            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 flex-wrap">
              Tap <Share className="w-3.5 h-3.5 inline" /> Share, then <span className="inline-flex items-center gap-0.5">"Add to Home Screen" <Plus className="w-3 h-3" /></span> for app-like access &amp; notifications.
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-0.5">Add it to your home screen for faster access and notifications.</p>
          )}
          {!iosHint && (
            <Button size="sm" onClick={install} data-testid="install-app-btn"
              className="mt-2 h-8 bg-[#1B4332] hover:bg-[#14472F]">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Install app
            </Button>
          )}
        </div>
        <button onClick={dismiss} data-testid="install-dismiss-btn" className="p-1 text-gray-400 hover:text-gray-700 shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
